/**
 * TariffComparisonTable - Comparison table for tariff results.
 *
 * Columns: Taryfa, Koszt bez PV, Koszt z PV, Oszczędności.
 * Best tariff highlighted. Zero-cost cells show tooltip explanation.
 * Expandable rows show per-tariff savings waterfall breakdown.
 */

import { useState, Fragment } from 'react';
import { Table, Text, Badge, Tooltip, ActionIcon, Collapse, Group, Stack, Divider } from '@mantine/core';
import { IconInfoCircle, IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import type { ProsumerTariffComparison } from '../../types';
import { TariffType } from '../../types';

export interface TariffComparisonTableProps {
  comparisons: ProsumerTariffComparison[];
  recommendedTariff: TariffType;
  currentTariff?: TariffType | '';
}

const ZERO_COST_TOOLTIP =
  'Nadwyżka energii oddanej do sieci generuje depozyty RCE. ' +
  'Roczny zwrot nadpłaty (20–30% wartości oddanej energii) pokrywa stałe opłaty dystrybucyjne. ' +
  'Rzeczywisty roczny koszt zbliżony do 0 PLN.';

function ZeroCostCell() {
  return (
    <Tooltip
      label={ZERO_COST_TOOLTIP}
      withArrow
      multiline
      w={300}
      events={{ hover: true, focus: true, touch: true }}
      data-testid="zero-cost-tooltip"
    >
      <Text
        component="span"
        style={{ cursor: 'help', display: 'inline-flex', alignItems: 'center', gap: 4 }}
        data-testid="zero-cost-cell"
      >
        ≈ 0 PLN <IconInfoCircle size={14} opacity={0.6} />
      </Text>
    </Tooltip>
  );
}

function TariffWaterfall({ comp }: { comp: ProsumerTariffComparison }) {
  const pr = comp.prosumerResult;
  if (!pr) return null;

  const hasAggregates =
    pr.totalDepositCreatedPln !== undefined ||
    pr.overpaymentRefundPln !== undefined ||
    pr.totalDepositExpiredPln !== undefined;

  if (!hasAggregates) {
    return (
      <Text size="xs" c="dimmed" py="xs">
        Szczegółowy podział oszczędności niedostępny.
      </Text>
    );
  }

  const depositCreated = pr.totalDepositCreatedPln ?? 0;
  const refund = pr.overpaymentRefundPln ?? 0;
  const expired = pr.totalDepositExpiredPln ?? 0;
  const costWithPV = comp.costWithPV;
  const savings = comp.savingsPln;

  return (
    <Stack gap={4} py="xs" px="md">
      <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={4}>
        Analiza oszczędności
      </Text>
      <Group justify="space-between">
        <Text size="xs">Koszt bez PV</Text>
        <Text size="xs" fw={500}>{Math.round(comp.costWithoutPV)} PLN</Text>
      </Group>
      {depositCreated > 0 && (
        <Group justify="space-between">
          <Text size="xs" c="green">Depozyty RCE wygenerowane</Text>
          <Text size="xs" c="green" fw={500}>−{Math.round(depositCreated)} PLN</Text>
        </Group>
      )}
      {refund > 0 && (
        <Group justify="space-between">
          <Text size="xs" c="green">Zwrot nadpłaty RCE (20–30%)</Text>
          <Text size="xs" c="green" fw={500}>−{Math.round(refund)} PLN</Text>
        </Group>
      )}
      {expired > 0 && (
        <Group justify="space-between">
          <Text size="xs" c="dimmed">Depozyty wygasłe (utracone)</Text>
          <Text size="xs" c="dimmed" fw={500}>+{Math.round(expired)} PLN</Text>
        </Group>
      )}
      <Divider my={4} />
      <Group justify="space-between">
        <Text size="xs" fw={600}>Koszt z PV</Text>
        {costWithPV < 1 ? <ZeroCostCell /> : <Text size="xs" fw={600}>{Math.round(costWithPV)} PLN</Text>}
      </Group>
      <Group justify="space-between">
        <Text size="xs" fw={600} c={savings > 0 ? 'green' : 'red'}>Oszczędności</Text>
        <Text size="xs" fw={600} c={savings > 0 ? 'green' : 'red'}>{savings > 0 ? '+' : ''}{Math.round(savings)} PLN ({comp.savingsPercent.toFixed(1)}%)</Text>
      </Group>
    </Stack>
  );
}

export function TariffComparisonTable({ comparisons, recommendedTariff, currentTariff }: TariffComparisonTableProps) {
  const [expandedTariff, setExpandedTariff] = useState<TariffType | null>(null);

  return (
    <Table striped highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Taryfa</Table.Th>
          <Table.Th>Koszt bez PV (PLN/rok)</Table.Th>
          <Table.Th>Koszt z PV (PLN/rok)</Table.Th>
          <Table.Th>Oszczędności</Table.Th>
          <Table.Th style={{ width: 36 }} />
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {comparisons.map((comp) => {
          const isRecommended = comp.tariffType === recommendedTariff;
          const isCurrent = currentTariff !== '' && comp.tariffType === currentTariff;
          const isExpanded = expandedTariff === comp.tariffType;
          const hasDetails = !!comp.prosumerResult;
          const rowStyle = isRecommended
            ? { backgroundColor: 'var(--mantine-color-green-0)' }
            : isCurrent
              ? { backgroundColor: 'var(--mantine-color-blue-0)' }
              : undefined;

          return (
            <Fragment key={comp.tariffType}>
              <Table.Tr style={rowStyle}>
                <Table.Td>
                  <Text fw={isRecommended || isCurrent ? 700 : 400}>
                    {comp.tariffType}
                    {isRecommended && (
                      <Badge component="span" ml="xs" size="xs" color="green" variant="light">
                        Rekomendacja
                      </Badge>
                    )}
                    {isCurrent && !isRecommended && (
                      <Badge component="span" ml="xs" size="xs" color="blue" variant="light">
                        Aktualna
                      </Badge>
                    )}
                  </Text>
                </Table.Td>
                <Table.Td>{Math.round(comp.costWithoutPV)}</Table.Td>
                <Table.Td>
                  {comp.costWithPV < 1 ? <ZeroCostCell /> : Math.round(comp.costWithPV)}
                </Table.Td>
                <Table.Td>
                  <Text c={comp.savingsPln > 0 ? 'green' : 'red'} fw={500}>
                    {comp.savingsPln > 0 ? '+' : ''}{Math.round(comp.savingsPln)} PLN ({comp.savingsPercent.toFixed(1)}%)
                  </Text>
                </Table.Td>
                <Table.Td>
                  {hasDetails && (
                    <ActionIcon
                      size="sm"
                      variant="subtle"
                      color="gray"
                      aria-label={isExpanded ? 'Zwiń szczegóły' : 'Rozwiń szczegóły'}
                      aria-expanded={isExpanded}
                      data-testid={`expand-${comp.tariffType}`}
                      onClick={() => setExpandedTariff(isExpanded ? null : comp.tariffType)}
                    >
                      {isExpanded ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
                    </ActionIcon>
                  )}
                </Table.Td>
              </Table.Tr>
              {hasDetails && (
                <Table.Tr key={`${comp.tariffType}-details`} style={{ padding: 0 }}>
                  <Table.Td colSpan={5} style={{ padding: 0 }}>
                    <Collapse in={isExpanded}>
                      <TariffWaterfall comp={comp} />
                    </Collapse>
                  </Table.Td>
                </Table.Tr>
              )}
            </Fragment>
          );
        })}
      </Table.Tbody>
    </Table>
  );
}
